import {
    DalItem,
    ExtensionPublicAPI,
    ValidateValue,
    pointerUtils,
    type CreateExtArgs,
    type DmApis,
    type Extension,
    type ExtensionAPI
} from '@wix/document-manager-core'
import type {
    BuilderStyle,
    NewGlobalStyleArg,
    GlobalStyleDefinition,
    CompRef,
    DesignObject,
    Pointer,
    StyleRef,
    VariantPointer,
    VariantRelation
} from '@wix/document-services-types'
import _ from 'lodash'
import {CSS_SHORTHAND_MAPPINGS} from '../builderComponents/shorthandMappings'
import type {PageExtensionAPI} from '../page'
import {fromStylableColor, regularColorRegExp, stylableColorsRegExp, toStylableColor} from '../../utils/colors'
import {deepFind} from '../../utils/object'
import {
    DATA_TYPES,
    GLOBAL_STYLES,
    MASTER_PAGE_ID,
    PAGE_NUMBER_LIMIT_FOR_STYLE_PER_PAGE,
    STYLES,
    VIEW_MODES
} from '../../constants/constants'
import {createMemo} from '../../utils/memoize'
import type {RMApi} from '../rendererModel'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import {ReportableError} from '@wix/document-manager-utils'
import {addHash, refArray, stripHashIfExists, variantRelation} from '../../utils/refArrayUtils'
import {isStructurePointer} from '../structure'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {RelationshipsAPI} from '../relationships'
import type {CssStyleExtensionAPI} from '../cssStyle/cssStyle'
import type {VariablesExtensionAPI} from '../variables/variables'
import type {VariantsExtensionAPI} from '../variants/variants'

interface StylesApi extends ExtensionAPI {
    replaceColorInAllItems(
        oldColor: string,
        newColor: string,
        pageId: string,
        predicate?: (pointer: Pointer) => boolean
    ): void
    connectToGlobalStyle(comp: Pointer, globalStylePointerOrId: Pointer | string): void
    getColorsListInAllComponents(): string[]
    isStylePerPageEnabled(): boolean
}

interface GlobalStyleApi extends ExtensionAPI {
    getPointer(pointerOrIdOrDef: Pointer | GlobalStyleDefinition | string): Pointer
    get(globalStylePointer: Pointer): BuilderStyle | undefined
    list(): Pointer[]
    getNewGlobalStylePointer(customGlobalStyleId?: string): Pointer
    create(newGlobalStyleId: string, globalStyleItem: NewGlobalStyleArg): Pointer
    update(
        globalStylePointer: Pointer,
        globalStyleItem: Partial<BuilderStyle>,
        shouldRemoveRelatedProperties?: boolean
    ): void
    remove(globalStylePointer: Pointer, paths: string[]): void
    delete(globalStylePointer: Pointer): void
    replace(oldGlobalStylePointer: Pointer, newGlobalStylePointer: Pointer): void
    getComponentsUsingStyle(globalStylePointer: Pointer, pageIds: string[]): CompRef[]
    isGlobalStyle(pointerOrId: Pointer | string): boolean
    definition: {
        get(globalStylePointerOrId: Pointer | string): GlobalStyleDefinition
        update(
            globalStylePointerOrId: Pointer | string,
            styleDefinition: Partial<Pick<GlobalStyleDefinition, 'name' | 'innerElementStyles'>>
        ): void
    }
}

export interface StylesExtensionApi extends ExtensionAPI {
    style: StylesApi & {
        global: GlobalStyleApi
    }
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, pointers, dal}: CreateExtArgs): StylesExtensionApi => {
        const {dataModel, page, rendererModel, cssStyle, variables, variants} = extensionAPI as DataModelExtensionAPI &
            PageExtensionAPI &
            RMApi &
            CssStyleExtensionAPI &
            VariablesExtensionAPI &
            VariantsExtensionAPI
        const {memoize} = createMemo()

        const getColorsListInAllComponents = () => {
            const usedColors = new Set<string>()

            const checkAndCollectColors = (item: StyleRef | DesignObject): boolean => {
                const colors: string[] = []
                _.cloneDeepWith(item, value => {
                    if (typeof value !== 'string') {
                        return
                    }
                    const matched = value.match(regularColorRegExp)
                    if (matched) colors.push(...matched)
                })

                colors?.forEach(colorId => usedColors.add(colorId))
                return colors.length > 0
            }

            const checkAndCollectStylableColors = (item: StyleRef) => {
                const stcss: string | undefined = item.style?.properties?.['$st-css']
                const matched = stcss?.match(stylableColorsRegExp)
                matched
                    ?.map(colorId => fromStylableColor(colorId))
                    .filter((colorId): colorId is string => !!colorId)
                    .forEach(colorId => usedColors.add(colorId))
                return matched
            }

            const pageIds = page.getAllPagesIds(true)

            pageIds.forEach(pageId => {
                pointers.data.getStyleItemsWithPredicate(
                    (styleObj: StyleRef) => styleObj.type === 'ComponentStyle' && checkAndCollectColors(styleObj),
                    pageId
                )
                pointers.data.getStyleItemsWithPredicate(checkAndCollectStylableColors, pageId)
                pointers.data.getDesignItemsWithPredicate(
                    (designObj: DesignObject) => checkAndCollectColors(designObj),
                    pageId
                )
            })

            return Array.from(usedColors)
        }

        const replaceColorInAllItems = (
            oldColor: string,
            newColor: string,
            pageId: string,
            predicate?: (pointer: Pointer) => boolean
        ) => {
            const regularRegExp = new RegExp(`${oldColor}(?!\\d)`, 'g')
            const oldStylableColor = toStylableColor(oldColor)
            const newStylableColor = toStylableColor(newColor)
            const stylableRegExp = oldStylableColor && new RegExp(`${oldStylableColor}`, 'g')

            const replaceRegularColors = (pointer: Pointer) => {
                const styleData = dal.get(pointer)
                const updatedStyleData = _.cloneDeepWith(styleData, value =>
                    typeof value === 'string' ? value.replace(regularRegExp, newColor) : undefined
                )
                dal.set(pointer, updatedStyleData)
            }

            const replacerStylableColors = (pointer: Pointer) => {
                const stcssPointer = pointers.getInnerPointer(pointer, ['style', 'properties', '$st-css'])
                const stcss: string = dal.get(stcssPointer)
                if (!stcss || !stylableRegExp || !newStylableColor) {
                    return
                }
                const updatedStCss = stcss.replace(stylableRegExp, newStylableColor)
                dal.set(stcssPointer, updatedStCss)
            }

            if (stylableRegExp) {
                const stylableItemWithPointers = pointers.data.getStyleItemsWithPredicate(
                    itemData => itemData.style?.properties?.['$st-css']?.match(stylableRegExp),
                    pageId
                )
                // replace in stylables
                stylableItemWithPointers
                    ?.filter(pointer => !predicate || predicate(pointer))
                    .forEach(pointer => replacerStylableColors(pointer))
            }

            const styleItemWithPointers = pointers.data.getStyleItemsWithPredicate(
                styleDalItem => deepFind(styleDalItem, value => typeof value === 'string' && regularRegExp.test(value)),
                pageId
            )
            // replace in styleItems
            styleItemWithPointers
                .filter(pointer => !predicate || predicate(pointer))
                .forEach(pointer => replaceRegularColors(pointer))

            const designItemsPointers = pointers.data.getDesignItemsWithPredicate(
                designDalItem =>
                    deepFind(designDalItem, value => typeof value === 'string' && regularRegExp.test(value)),
                pageId
            )
            // replace in desingItems
            designItemsPointers
                .filter(pointer => !predicate || predicate(pointer))
                .forEach(pointer => replaceRegularColors(pointer))
        }

        const isStylePerPageEnabled = () => {
            const stylesPerPage = dal.get({
                type: DATA_TYPES.data,
                id: MASTER_PAGE_ID,
                innerPath: ['styleSettings', 'stylesPerPage']
            })
            const initialPages = rendererModel.getInitialPageList()?.pages ?? []
            const initialNumPages = initialPages.length

            if (stylesPerPage && parseFloat(stylesPerPage) >= parseFloat(STYLES.STYLES_PER_PAGE_VER)) {
                return true
            }

            return initialNumPages <= PAGE_NUMBER_LIMIT_FOR_STYLE_PER_PAGE
        }

        const getGlobalStylePointer = (pointerOrIdOrDef: Pointer | GlobalStyleDefinition | string) => {
            if (typeof pointerOrIdOrDef === 'string') {
                return pointers.data.getThemeItem(stripHashIfExists(pointerOrIdOrDef), MASTER_PAGE_ID)
            }
            if (pointerOrIdOrDef?.type === 'GlobalStyle') {
                return pointers.data.getThemeItem(pointerOrIdOrDef.id, MASTER_PAGE_ID)
            }

            return pointerOrIdOrDef
        }

        const isGlobalStyle = (pointerOrId: Pointer | string): boolean => {
            const globalStylePointer =
                typeof pointerOrId === 'string' ? pointers.data.getThemeItem(pointerOrId, MASTER_PAGE_ID) : pointerOrId

            return dal.get(globalStylePointer)?.type === 'GlobalStyle'
        }

        const validateInnerElementStyles = (globalStyleItem: any) => {
            if (globalStyleItem.innerElementStyles) {
                Object.keys(globalStyleItem.innerElementStyles).forEach(styleId => {
                    if (!isGlobalStyle(styleId)) {
                        throw new Error(`${styleId} is not a recognized global style`)
                    }
                })
            }
        }

        const getGlobalStyleDefinition: GlobalStyleApi['definition']['get'] = (
            globalStylePointerOrId: Pointer | string
        ) => {
            const globalStylePointer = getGlobalStylePointer(globalStylePointerOrId)
            const globalStyleDefinition = dal.get(globalStylePointer)
            if (!globalStyleDefinition) {
                throw new Error(`Global style does exist: ${globalStylePointer.id}`)
            }
            return _.pick(globalStyleDefinition, ['name', 'id', 'styleType', 'innerElementStyles', 'type'])
        }

        const updateGlobalStyleDefinition = (
            globalStylePointerOrId: Pointer | string,
            updatedGlobalStyleDefinition: Partial<Pick<GlobalStyleDefinition, 'name' | 'innerElementStyles'>>
        ) => {
            const updatedFields = _.pick(updatedGlobalStyleDefinition, ['name', 'innerElementStyles'])
            if (_.size(updatedFields)) {
                validateInnerElementStyles(updatedFields)
                const globalStylePointer = getGlobalStylePointer(globalStylePointerOrId)
                const definition = dal.get(globalStylePointer)
                dal.set(globalStylePointer, {...definition, ...updatedFields})
            }
        }

        const hasDataForGlobalStylePointer = (globalStylePointer: Pointer): boolean => {
            return dal.has(globalStylePointer)
            // eventually when we support variants in global style:
            //
            // if (!globalStylePointer.variants?.length) {
            //     //because dal.has was true, and there are no variants
            //     return true
            // }
            // const variantItemPointer = variants.getDataPointerConsideringVariants(
            //     globalStylePointer,
            //     DATA_TYPES.theme,
            //     'value'
            // )
            // return Boolean(variantItemPointer && dal.has(variantItemPointer))
        }

        const getGlobalCSSStyle = (globalStylePointer: Pointer): BuilderStyle | undefined => {
            if (hasDataForGlobalStylePointer(globalStylePointer)) {
                return cssStyle.getPublicCSSStyle(
                    variants.getItemConsideringVariantsForPointer(globalStylePointer, DATA_TYPES.theme, 'value')
                )
            }
        }

        const globalStylesArrayPointer = pointerUtils.getInnerPointer(
            pointers.data.getThemeItem(GLOBAL_STYLES, MASTER_PAGE_ID),
            'styles'
        )

        const getGlobalStylesList = (): string[] => {
            const globalStylesArray = dal.get(globalStylesArrayPointer)

            return globalStylesArray ?? []
        }

        const listAllGlobalStyles = () => {
            const globalStylesCollection = getGlobalStylesList()
            return globalStylesCollection.map(id => getGlobalStylePointer(id))
        }

        const getNewGlobalStylePointer = (customGlobalStyleId?: string) => {
            const newGlobalStyleId = customGlobalStyleId ?? dataModel.generateItemIdWithPrefix('global')

            if (dal.has(getGlobalStylePointer(newGlobalStyleId))) {
                throw new ReportableError({
                    errorType: 'GLOBAL_STYLE_ALREADY_EXIST',
                    message: `Global style with id ${newGlobalStyleId} already exists.`,
                    extras: {
                        globalStyleId: newGlobalStyleId
                    }
                })
            }

            return pointers.data.getThemeItem(newGlobalStyleId, MASTER_PAGE_ID)
        }

        const createNewGlobalStyleItem = (
            id: string,
            globalStyleItem: {name?: string; type: string; innerElementStyles?: Record<string, string[][]>}
        ) => {
            return {
                id,
                type: 'GlobalStyle',
                styleType: globalStyleItem.type,
                ...(globalStyleItem.name ? {name: globalStyleItem.name} : {}),
                ...(globalStyleItem.innerElementStyles ? {innerElementStyles: globalStyleItem.innerElementStyles} : {})
            }
        }

        const createGlobalStyle = (newGlobalStyleId: string, newGlobalStyleItem: NewGlobalStyleArg) => {
            const styleDataItem = dataModel.createStyleItemByType(newGlobalStyleItem.type!)

            if (!styleDataItem) {
                throw new ReportableError({
                    errorType: 'GLOBAL_STYLE_SCHEMA_NOT_FOUND',
                    message: `Global style schema not found for type ${newGlobalStyleItem.type}.`,
                    extras: {
                        globalStyleId: newGlobalStyleId
                    }
                })
            }

            validateInnerElementStyles(newGlobalStyleItem)

            const styleItemWithUpdatedVariableConnections = variables.updateCssVariableConnections({
                ...styleDataItem,
                ..._.omit(newGlobalStyleItem, ['name', 'innerElementStyles'])
            })

            const globalStyleItem = createNewGlobalStyleItem(newGlobalStyleId, newGlobalStyleItem)
            const globalStylesCollectionPointer = pointers.data.getThemeItem(GLOBAL_STYLES, MASTER_PAGE_ID)

            //Add global style item to collection:
            if (dal.has(globalStylesCollectionPointer)) {
                dataModel.addItem(globalStyleItem, DATA_TYPES.theme, MASTER_PAGE_ID, newGlobalStyleId)

                const globalStylesArray = dal.get(globalStylesArrayPointer)

                dal.set(globalStylesArrayPointer, globalStylesArray.concat(addHash(newGlobalStyleId)))
            } else {
                const globalStyleCollection = dataModel.createStyleItemByType('GlobalStylesCollection') as any

                globalStyleCollection.styles = [globalStyleItem]

                dataModel.addItem(globalStyleCollection, DATA_TYPES.theme, MASTER_PAGE_ID, GLOBAL_STYLES)
            }

            //update default value:
            variants.updateDataConsideringVariants({
                pointerWithVariants: pointers.data.getThemeItem(newGlobalStyleId, MASTER_PAGE_ID),
                valueToUpdate: styleItemWithUpdatedVariableConnections,
                namespace: DATA_TYPES.theme,
                referenceName: 'value'
            })
            return pointers.data.getThemeItem(newGlobalStyleId, MASTER_PAGE_ID)
        }

        const update = (
            globalStylePointer: Pointer,
            partialStyle: BuilderStyle,
            shouldRemoveRelatedProperties = true
        ) => {
            let styleItem = getGlobalCSSStyle(globalStylePointer)
            if (!styleItem) {
                const {styleType} = dal.get(globalStylePointer)
                styleItem = {type: styleType}
            }

            const styleToUpdate = cssStyle.getUpdatedCSSStyleItemForUpdate(
                styleItem,
                partialStyle,
                shouldRemoveRelatedProperties
            )
            variants.updateDataConsideringVariants({
                pointerWithVariants: globalStylePointer,
                valueToUpdate: styleToUpdate,
                namespace: DATA_TYPES.theme,
                referenceName: 'value'
            })
        }

        const remove = (globalStylePointer: Pointer, paths: string[]) => {
            const styleItem = getGlobalCSSStyle(globalStylePointer) as BuilderStyle
            const styleToUpdate = cssStyle.getUpdatedCSSStyleItemForRemove(styleItem, paths)
            if (styleToUpdate) {
                variants.updateDataConsideringVariants({
                    pointerWithVariants: globalStylePointer,
                    valueToUpdate: styleToUpdate,
                    namespace: DATA_TYPES.theme,
                    referenceName: 'value'
                })
            }
        }

        const getPointerWithVariants = (pointer: Pointer, relation: VariantRelation): Pointer => {
            const pageId: string = pointers.data.getPageIdOfData(pointer)
            const variantsPointers: VariantPointer[] = variantRelation
                .extractVariants(relation)
                .map(variantId => pointers.data.getVariantsDataItem(variantId, pageId))

            return pointers.variants.getPointerWithVariants(pointer, variantsPointers)
        }

        const getReferencesToPointerWithVariants = (pointer: Pointer, pageIds: Set<string>): Pointer[] => {
            const {relationships} = extensionAPI as RelationshipsAPI

            const references = relationships
                .getAllReferencesToPointer(pointer)
                .filter(p => pageIds.has(pointers.data.getPageIdOfData(p)))
                .map(p => pointers.variants.inheritVariantsFromPointer(p, pointer))

            const item: DalItem = dal.get(pointer)
            if (variantRelation.isVariantRelation(item)) {
                const compPointer = pointers.components.getComponentById(
                    variantRelation.extractFrom(item),
                    VIEW_MODES.DESKTOP
                )
                return [getPointerWithVariants(compPointer, item)]
            }

            return references
        }

        const getComponentsReferencingPointer = (pointer: Pointer, pageIds: Set<string>): CompRef[] => {
            if (isStructurePointer(pointer)) {
                return [pointer]
            }

            const references = getReferencesToPointerWithVariants(pointer, pageIds)
            return references.flatMap(p => getComponentsReferencingPointer(p, pageIds))
        }

        const getComponentsUsingStyle = (globalStylePointer: Pointer, pageIds: string[]): CompRef[] => {
            if (!isGlobalStyle(globalStylePointer)) {
                throw new Error('Invalid parameter: should be a global style pointer')
            }

            return getComponentsReferencingPointer(globalStylePointer, new Set(pageIds))
        }

        const deleteStyle = (globalStylePointer: Pointer) => {
            const componentsUsingStyle = getComponentsUsingStyle(globalStylePointer, page.getAllPagesIds(true))

            if (componentsUsingStyle.length > 0) {
                throw new ReportableError({
                    errorType: 'GLOBAL_STYLE_IN_USE',
                    message: `Cannot remove global style ${globalStylePointer.id} because it is used by components.`,
                    extras: {
                        globalStyleId: globalStylePointer.id,
                        componentsUsingStyle
                    }
                })
            }

            const globalStylesList = getGlobalStylesList()

            const updatedGlobalStylesList = globalStylesList.filter(
                styleId => styleId !== addHash(globalStylePointer.id)
            )

            if (updatedGlobalStylesList.length === globalStylesList.length) {
                return
            }

            dal.set(globalStylesArrayPointer, updatedGlobalStylesList)

            dataModel.removeItemRecursively(globalStylePointer)
        }

        const replace = (oldGlobalStylePointer: Pointer, newGlobalStylePointer: Pointer) => {
            const {relationships} = extensionAPI as RelationshipsAPI

            const owningReferences = relationships.getOwningReferencesToPointer(oldGlobalStylePointer)

            owningReferences.forEach((owningReference: Pointer) => {
                if (owningReference.id === GLOBAL_STYLES) {
                    return
                }

                const dataItem = deepClone(dal.get(owningReference))

                if (variantRelation.isVariantRelation(dataItem) && dataItem.to === `#${oldGlobalStylePointer.id}`) {
                    dataItem.to = `#${newGlobalStylePointer.id}`
                } else if (refArray.isRefArray(dataItem)) {
                    dataItem.values = dataItem.values.map((value: string) => {
                        if (value === `#${oldGlobalStylePointer.id}`) {
                            return `#${newGlobalStylePointer.id}`
                        }
                        return value
                    })
                } else if (dataItem.styleId === oldGlobalStylePointer.id) {
                    dataItem.styleId = newGlobalStylePointer.id
                }

                dal.set(owningReference, dataItem)
            })
        }

        //comp can be a component pointer or innerElement pointer
        const connectToGlobalStyle = (comp: Pointer, globalStylePointerOrId: Pointer | string) => {
            if (!isGlobalStyle(globalStylePointerOrId)) {
                throw new Error('connectToGlobalStyle called with a style which is not a global style!')
            }

            const globalStylePointer =
                typeof globalStylePointerOrId === 'string'
                    ? getGlobalStylePointer(globalStylePointerOrId)
                    : (globalStylePointerOrId as Pointer)

            variants.updateDataConsideringVariants({
                pointerWithVariants: comp,
                valueToUpdate: globalStylePointer,
                namespace: DATA_TYPES.theme,
                referenceName: 'styleId',
                customUpdater: () => globalStylePointer
            })
        }

        return {
            style: {
                isStylePerPageEnabled: memoize<StylesApi['isStylePerPageEnabled']>(isStylePerPageEnabled),
                replaceColorInAllItems,
                getColorsListInAllComponents,
                connectToGlobalStyle,
                global: {
                    getPointer: getGlobalStylePointer,
                    get: getGlobalCSSStyle,
                    list: listAllGlobalStyles,
                    getNewGlobalStylePointer,
                    create: createGlobalStyle,
                    update,
                    remove,
                    delete: deleteStyle,
                    replace,
                    getComponentsUsingStyle,
                    isGlobalStyle,
                    definition: {
                        get: getGlobalStyleDefinition,
                        update: updateGlobalStyleDefinition
                    }
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): ExtensionPublicAPI => {
        const {style} = extensionAPI as StylesExtensionApi

        return {
            style: {
                /**
                 * The function replaces all the occurrences of colorId in style objects and design items
                 * @param {oldColor} color that needs to be replaced
                 * @param {newColor} color that replaces an old color
                 */
                replaceColorInAllComponents: style.replaceColorInAllItems,
                /**
                 * The function returns all colors that are used in site components
                 */
                getColorsListInAllComponents: style.getColorsListInAllComponents,
                connectToGlobalStyle: (compRef: CompRef, globalStylePointerOrId: Pointer | string) => {
                    style.connectToGlobalStyle(compRef, globalStylePointerOrId)
                },
                CSS_SHORTHAND_MAPPINGS: Object.entries(CSS_SHORTHAND_MAPPINGS).reduce(
                    (cssShorthandMapping, [shortHandKey, shortHandExpansion]) => {
                        cssShorthandMapping[shortHandKey] = shortHandExpansion.map(expansion => expansion.propertyName)
                        return cssShorthandMapping
                    },
                    {} as Record<string, string[]>
                ),
                global: {
                    getPointer: (pointerOrIdOrDef: Pointer | GlobalStyleDefinition | string) =>
                        style.global.getPointer(pointerOrIdOrDef),
                    create: (newGlobalStylePointer: Pointer, globalStyleItem: NewGlobalStyleArg) =>
                        style.global.create(newGlobalStylePointer.id, globalStyleItem),
                    update: (
                        globalStylePointer: Pointer,
                        globalStyleItem: BuilderStyle,
                        shouldRemoveRelatedProperties?: boolean
                    ) => style.global.update(globalStylePointer, globalStyleItem, shouldRemoveRelatedProperties),
                    remove: (globalStylePointer: Pointer, paths: string[]) =>
                        style.global.remove(globalStylePointer, paths),
                    delete: (globalStylePointer: Pointer) => style.global.delete(globalStylePointer),
                    getComponentsUsingStyle: (globalStylePointer: Pointer, pageIds: string[]) =>
                        style.global.getComponentsUsingStyle(globalStylePointer, pageIds),
                    isGlobalStyle: (pointerOrId: Pointer | string) => style.global.isGlobalStyle(pointerOrId),
                    getNewGlobalStylePointer: (globalStyleItem: BuilderStyle, customGlobalStyleId?: string) =>
                        style.global.getNewGlobalStylePointer(customGlobalStyleId),
                    replace: (oldGlobalStylePointer: Pointer, newGlobalStylePointer: Pointer) =>
                        style.global.replace(oldGlobalStylePointer, newGlobalStylePointer),
                    get: (globalStylePointer: Pointer) => style.global.get(globalStylePointer),
                    list: () => style.global.list(),
                    definition: {
                        get: (globalStylePointer: Pointer) => style.global.definition.get(globalStylePointer),
                        update: (
                            globalStylePointerOrId: Pointer | string,
                            styleDefinition: Partial<GlobalStyleDefinition>
                        ) => {
                            style.global.definition.update(globalStylePointerOrId, styleDefinition)
                        }
                    }
                }
            }
        }
    }

    const createValidator = ({dal, extensionAPI, pointers}: DmApis): Record<string, ValidateValue> => ({
        validateBuilderComponentStyle: (pointer: Pointer, value: DalItem) => {
            const {style} = extensionAPI as StylesExtensionApi

            const validateStyleTypeAgainstBuilderComponent = (styleId?: string, componentType?: string) => {
                if (!styleId || !componentType || !style.global.isGlobalStyle(styleId)) {
                    return
                }
                const styleItem = dal.get(pointers.data.getThemeItem(styleId))
                if (!styleItem) {
                    return
                }
                const expectedCompStyle = `${componentType}.Style`
                const isGlobalStyle = styleItem.type === 'GlobalStyle'
                if (
                    (isGlobalStyle && styleItem.styleType !== expectedCompStyle) ||
                    (!isGlobalStyle && styleItem.type !== expectedCompStyle)
                ) {
                    return [
                        {
                            shouldFail: true,
                            type: 'STYLE_TYPE_MISMATCH',
                            message: `The style type ${styleItem.type} is not derived from the component manifest. The component type is ${componentType}`
                        }
                    ]
                }
            }

            if (!value) {
                return
            }

            if (pointer.type === DATA_TYPES.theme && variantRelation.isVariantRelation(value)) {
                const styleId = variantRelation.extractTo(value)
                const componentId = variantRelation.extractFrom(value)
                const component = dal.get(pointerUtils.getPointer(componentId, VIEW_MODES.DESKTOP))

                return validateStyleTypeAgainstBuilderComponent(styleId, component?.componentType)
            } else if (isStructurePointer(pointer)) {
                return validateStyleTypeAgainstBuilderComponent(value.styleId, value.componentType)
            }
        }
    })

    return {
        name: 'style',
        createExtensionAPI,
        createPublicAPI,
        createValidator
    }
}

export {createExtension}
